import { forwardRef, memo, useCallback, useMemo } from 'react';
import map from 'lodash/map';
import size from 'lodash/size';
import { useIntl, FormattedDate, FormattedMessage } from 'react-intl';
// Material UI imports
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import CircularProgress from '@mui/material/CircularProgress';
// Material Icon imports
import StarRounded from '@mui/icons-material/StarRounded';
// Skillmore UI Components
import { shape } from '@empathco/ui-components/src/styles/themeOptions';
import { longDateOptions } from '@empathco/ui-components/src/common/intl';
import { getJsDateFromISO } from '@empathco/ui-components/src/helpers/datetime';
import BoxTypography from '@empathco/ui-components/src/mixins/BoxTypography';
import TruncatedTextLink from '@empathco/ui-components/src/elements/TruncatedTextLink';
import SkillName from '@empathco/ui-components/src/elements/SkillName';
import TagLabel from '@empathco/ui-components/src/elements/TagLabel';
import ItemCard from '@empathco/ui-components/src/elements/ItemCard';
// local imports
import { SkillLevel } from '../models/skill';
import { DevPlanCompletedCourse, DevPlanCourse, EmployeeDevPlanCourse } from '../graphql/customTypes';
import { currentEmployeeDetails, useMixpanel } from '../context/analytics';
import useCustomerSettings from '../config/customer';
import { MAX_COURSE_TITLE_LENGTH } from '../config/params';
import { PATH_SKILL } from '../config/paths';
import CourseProviderLogo from '../elements/CourseProviderLogo';
import CourseFeatures from '../elements/CourseFeatures';
// SCSS imports
import { divider } from './DevPlanCourseCard.module.scss';

type DevPlanCourseCardProps = {
  readonly item: DevPlanCourse | EmployeeDevPlanCourse | DevPlanCompletedCourse;
  readonly logo?: string;
  readonly disabled?: boolean | null;
  readonly isSelected?: boolean;
  readonly isHighlighted?: boolean;
  readonly withCompletedState?: boolean;
  readonly onSelect?: (course: DevPlanCourse) => void;
  readonly onRemove?: (course: DevPlanCourse) => void;
  readonly onComplete?: (course: DevPlanCourse) => void;
  readonly onCompletePending?: boolean;
}

const DevPlanCourseCard = forwardRef<HTMLDivElement, DevPlanCourseCardProps>(({
  item,
  logo,
  disabled = false,
  isSelected,
  isHighlighted = false,
  withCompletedState = false,
  onSelect,
  onRemove,
  onComplete,
  onCompletePending = false
  // eslint-disable-next-line complexity
}, ref) => {
  const { formatMessage } = useIntl();

  const { getCourseUrl } = useCustomerSettings();
  const { code, title, link, provider, external_url, covered_skills, description } = item || {};
  const { is_selected } = (item || {}) as DevPlanCourse;
  const { is_complete, completed_at } = (item || {}) as DevPlanCompletedCourse;

  const ariaLabelPreferred = useMemo(() => formatMessage({ id: 'hr.dev_plan.preferred' }), [formatMessage]);
  const removeAriaLabel = useMemo(
    () => onRemove ? formatMessage({ id: 'common.button.remove_item' }, { name: title }) : undefined,
    [onRemove, formatMessage, title]
  );

  const handleChange = useCallback(() => onSelect?.(item as DevPlanCourse), [item, onSelect]);
  const handleRemove = useCallback(() => onRemove?.(item as DevPlanCourse), [item, onRemove]);
  const handleComplete = useCallback(() => onComplete?.(item as DevPlanCourse), [item, onComplete]);

  const pleLink = link || external_url || getCourseUrl(code);
  const cardIsSelected = isSelected || (onSelect ? Boolean(is_selected) : undefined);

  const mixpanel = useMixpanel();
  const handleLinkClick = useCallback(() => mixpanel.track('CourseLink', {
    ...currentEmployeeDetails, courseCode: code, courseTitle: title, provider: provider?.title, href: pleLink
  }), [mixpanel, code, title, provider, pleLink]);

  return (
    <ItemCard
        ref={ref}
        light
        header={isHighlighted && !cardIsSelected ? (
          <Box flexGrow={1} display="flex" alignItems="flex-start" justifyContent="space-between" color="misc.starRating">
            <TagLabel variant="course"/>
            <StarRounded color="inherit" fontSize="medium" aria-label={ariaLabelPreferred}/>
          </Box>
        ) : (
          <TagLabel variant="course"/>
        )}
        onSelect={onSelect ? handleChange : undefined}
        onRemoveClick={onRemove && !is_selected ? handleRemove : undefined}
        removeAriaLabel={removeAriaLabel}
        selected={cardIsSelected}
        disabled={disabled}
        status={withCompletedState && is_complete && completed_at ? (
          <BoxTypography pt={1} pl={0.25} variant="body2" fontStyle="italic">
            <FormattedMessage
                id="hr.dev_plan.course_completed"
                // eslint-disable-next-line react/jsx-props-no-spreading
                values={{ date: <FormattedDate value={getJsDateFromISO(completed_at)} {...longDateOptions}/> }}
            />
          </BoxTypography>
        ) : (onComplete && !is_complete && (
          <Box pt={1} display="flex" alignItems="flex-end" justifyContent="center">
            <Button
                color="secondary"
                variant="text"
                onClick={handleComplete}
                disabled={disabled || onCompletePending ? true : undefined}
                startIcon={onCompletePending ? <CircularProgress size={16} color="inherit"/> : undefined}
            >
              <FormattedMessage id="hr.dev_plan.button.mark_as_completed"/>
            </Button>
          </Box>
        )) || undefined}
    >
      {provider || logo ? (
        <>
          <CourseProviderLogo provider={provider} logo={logo}/>
          <Divider className={divider}/>
        </>
      ) : undefined}

      <Box py={1}>
        <TruncatedTextLink
            text={title}
            href={pleLink}
            onClick={handleLinkClick}
            variant="subtitle1"
            dark
            length={MAX_COURSE_TITLE_LENGTH}
            disabled={disabled ? true : undefined}
        />
      </Box>

      <Divider className={divider}/>

      <Box
          flex="1 1 0"
          py={1}
      >
        <CourseFeatures course={item as DevPlanCourse}/>
      </Box>

      {description ? (
        <Box pb={1}>
          <TruncatedTextLink
              text={description}
              plain
              regular
              color="text.secondary"
              variant="body2"
              disabled={disabled ? true : undefined}
          />
        </Box>
      ) : undefined}

      {size(covered_skills) >= 1 && (
        <>
          <Divider className={divider}/>

          <Box pt={1.125} display="flex" alignItems="center" flexWrap="wrap">
            {map(covered_skills, (skill) => (
              <Box
                  key={skill.id}
                  my={0.375}
                  mr={0.75}
                  px={1}
                  py={0.25}
                  bgcolor="background.card"
                  borderRadius={shape.tinyBorderRadius}
              >
                <SkillName
                    skill={skill}
                    level={skill.current_level as SkillLevel}
                    skillPath={PATH_SKILL}
                    variant="body1"
                    maxLines={1}
                    disabled={disabled}
                />
              </Box>
            ))}
          </Box>
        </>
      )}

    </ItemCard>
  );
});

DevPlanCourseCard.displayName = 'DevPlanCourseCard';

export default memo(DevPlanCourseCard);
